import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdSearch, 
  MdClose, 
  MdRefresh,
  MdBusinessCenter,
  MdInfo,
  MdChevronRight
} from 'react-icons/md';
import { FaFilePdf, FaFileExcel, FaFilter, FaCalendarAlt, FaUser, FaFileContract, FaBook, FaClock } from 'react-icons/fa';
  import jsPDF from 'jspdf';
  import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const CompanyReportPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    customer_id: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState([]);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Validation popup state
  const [showValidationPopup, setShowValidationPopup] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Müşterileri getir
  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/customers', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Rapor oluştur
  const generateReport = async () => {
    // En azından bir filtre kriteri seçilmiş olmalı
    const hasFilter = filters.customer_id;

    if (!hasFilter) {
      setShowValidationPopup(true);
      return;
    }

    try {
      setIsLoading(true);
      
      // API'den müşteri verilerini getir
      const response = await api.get('/customers', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let filteredData = response.data.filter(customer => {
        // Müşteri filtresi - sadece belirli müşteri seçilmişse uygula
        if (filters.customer_id && filters.customer_id !== 'all') {
          if (String(customer.id) !== String(filters.customer_id)) return false;
        }
        
        return true;
      });

      // Her müşteri için detay bilgileri al
      const enrichedData = [];
      for (const customer of filteredData) {
        try {
          // Müşterinin contract'larını al
          const contractsResponse = await api.get(`/customers/${customer.id}/contracts`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          // Müşterinin support case'lerini al
          const casesResponse = await api.get('/support-cases', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            params: { customer_id: customer.id }
          });
          
          // Case durumlarını say
          const openCases = casesResponse.data.filter(c => c.case_status === 'OPEN').length;
          const inProgressCases = casesResponse.data.filter(c => c.case_status === 'IN PROGRESS').length;
          const closedCases = casesResponse.data.filter(c => c.case_status === 'CLOSED').length;
          
          // Contract isimlerini al
          const contractNames = contractsResponse.data.map(c => c.contract_name).join(', ');
          
          enrichedData.push({
            ...customer,
            contracts: contractsResponse.data || [],
            cases: casesResponse.data || [],
            open_cases: openCases,
            in_progress_cases: inProgressCases,
            closed_cases: closedCases,
            contract_names: contractNames,
            total_contracts: contractsResponse.data.length,
            total_cases: casesResponse.data.length
          });
        } catch (detailError) {
          console.error(`Müşteri detayları alınırken hata (ID: ${customer.id}):`, detailError);
          
          enrichedData.push({
            ...customer,
            contracts: [],
            cases: [],
            open_cases: 0,
            in_progress_cases: 0,
            closed_cases: 0,
            contract_names: '',
            total_contracts: 0,
            total_cases: 0
          });
        }
      }
      
      setReportData(enrichedData);
      setReportGenerated(true);
      setCurrentPage(1); // Yeni rapor oluşturulduğunda ilk sayfaya dön
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(t('reports.reportGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel
    const excelData = reportData.map((item, index) => ({
      [t('reports.serialNumber')]: index + 1,
      [t('reports.companyName')]: cleanTextForExcel(item.customer_name),
      [t('reports.open')]: cleanTextForExcel(item.open_cases),
      [t('reports.inProgress')]: cleanTextForExcel(item.in_progress_cases),
      [t('reports.closed')]: cleanTextForExcel(item.closed_cases),
      [t('reports.contractCount')]: cleanTextForExcel(item.total_contracts),
      [t('reports.contactPerson')]: cleanTextForExcel(item.customer_contact),
      [t('reports.phone')]: cleanTextForExcel(item.customer_contact_phone),
      [t('reports.email')]: cleanTextForExcel(item.customer_contact_email),
      [t('reports.registrationDate')]: cleanTextForExcel(new Date(item.created_at).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR'))
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

         // Set column widths
     const colWidths = [
       { wch: 8 },   // Sira No
       { wch: 25 },  // Firma Adı
       { wch: 12 },  // Açık Case
       { wch: 15 },  // İşlemde Case
       { wch: 12 },  // Kapalı Case
       { wch: 15 },  // Contract Sayısı
       { wch: 25 },  // Yetkili Kişi
       { wch: 15 },  // Telefon
       { wch: 25 },  // Email
       { wch: 15 }   // Kayıt Tarihi
     ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('reports.companyReport'));

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.companyReport').replace(/\s+/g, '_')}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

        // PDF export  
   const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

           const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

     // Helper function to handle Turkish characters
     const cleanText = (text) => {
       if (!text) return '';
       
       // Convert Turkish characters to Latin equivalents for PDF compatibility
       const turkishToLatin = {
         'ç': 'c', 'Ç': 'C',
         'ğ': 'g', 'Ğ': 'G', 
         'ı': 'i', 'I': 'I',
         'İ': 'I', 'i': 'i',
         'ö': 'o', 'Ö': 'O',
         'ş': 's', 'Ş': 'S',
         'ü': 'u', 'Ü': 'U'
       };
       
       let cleanedText = text.toString().replace(/undefined/g, '');
       
       // Replace Turkish characters
       for (const [turkish, latin] of Object.entries(turkishToLatin)) {
         cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
       }
       
       return cleanedText;
     };

    // Add title
    doc.setFontSize(16);
    doc.text(t('reports.companyReport'), 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`${t('reports.reportDate')}: ${new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}`, 14, 30);

     // Prepare table data with clean text
     const tableData = reportData.map((item, index) => [
       index + 1,
       cleanText((item.customer_name || '').substring(0, 20) + (item.customer_name?.length > 20 ? '...' : '')),
       cleanText(item.open_cases) || '0',
       cleanText(item.in_progress_cases) || '0',
       cleanText(item.closed_cases) || '0',
       cleanText(item.total_contracts) || '0',
       cleanText((item.customer_contact || '').substring(0, 20) + (item.customer_contact?.length > 20 ? '...' : '')),
       cleanText((item.customer_contact_phone || '').substring(0, 15) + (item.customer_contact_phone?.length > 15 ? '...' : '')),
       cleanText((item.customer_contact_email || '').substring(0, 25) + (item.customer_contact_email?.length > 25 ? '...' : '')),
       cleanText(new Date(item.created_at).toLocaleDateString('tr-TR'))
     ]);

    // Add table
    autoTable(doc, {
      head: [['#', t('reports.companyName'), t('reports.open'), t('reports.inProgress'), t('reports.closed'), t('reports.contractCount'), t('reports.contactPerson'), t('reports.phone'), t('reports.email'), t('reports.registrationDate')]],
       body: tableData,
       startY: 40,
       styles: {
         fontSize: 7,
         cellPadding: 1,
         font: 'helvetica',
         fontStyle: 'normal'
       },
       headStyles: {
         fillColor: [66, 139, 202],
         textColor: 255,
         fontSize: 8,
         fontStyle: 'bold',
         font: 'helvetica'
       },
       alternateRowStyles: {
         fillColor: [245, 245, 245]
       },
               columnStyles: {
          0: { cellWidth: 12 },  // #
          1: { cellWidth: 40 },  // Firma Adi
          2: { cellWidth: 18 },  // Acik
          3: { cellWidth: 18 },  // Islemde
          4: { cellWidth: 18 },  // Kapali
          5: { cellWidth: 25 },  // Contract Sayisi
          6: { cellWidth: 35 },  // Yetkili Kisi
          7: { cellWidth: 30 },  // Telefon
          8: { cellWidth: 40 },  // Email
          9: { cellWidth: 25 }   // Kayit Tarihi
        },
               margin: { left: 10, right: 10 },
        tableWidth: '100%'
     });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.companyReport').replace(/\s+/g, '_')}_${currentDate}.pdf`;

     // Save file
     doc.save(filename);
   };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      customer_id: ''
    });
    setReportData([]);
    setReportGenerated(false);
    setCurrentPage(1);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değiştiğinde ilk sayfaya dön
  };

  return (
    <div className="h-full overflow-auto">
             {/* Breadcrumb */}
       <div className="flex justify-between items-center mb-6">
         <nav className="flex items-center text-sm">
           <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
             <span className="text-[#fff] font-medium">
               {t('common.reports')}
             </span>
             <MdChevronRight className="mx-2 text-[#fff]" size={20} />
             <span className="text-[#fff] font-medium">
               {t('reports.companyReport')}
             </span>
           </div>
         </nav>
       </div>

                            {/* Filter Panel */}
         <div className=" dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
           {/* Filter Panel Header */}
           <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
             <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.companyInfo')}</h1>
           </div>
           <div className="p-6">
             <div className="flex flex-col lg:flex-row lg:items-center gap-4">
             {/* Firma Seçimi */}
             <div className="flex-1">
               <select
                 value={filters.customer_id}
                 onChange={(e) => setFilters(prev => ({ ...prev, customer_id: e.target.value }))}
                 className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
               >
                <option value="">{t('reports.selectCompany')}</option>
                <option value="all">{t('reports.all')}</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:flex-shrink-0">
                             <button
                 onClick={generateReport}
                 disabled={isLoading}
                 className="flex items-center justify-center gap-2 px-4 py-2 bg-[#232b3b] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50"
               >
                <MdSearch size={16} />
                {isLoading ? t('reports.generating') : t('common.show')}
              </button>

              <button
                onClick={clearFilters}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                <MdClose size={16} />
                {t('reports.clearFilters')}
              </button>

                             <button
                 onClick={exportToExcel}
                 disabled={!reportGenerated || reportData.length === 0}
                 className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
               >
                <FaFileExcel size={16} />
                {t('reports.downloadExcel')}
              </button>

                             <button
                 onClick={exportToPDF}
                 disabled={!reportGenerated || reportData.length === 0}
                 className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
               >
                <FaFilePdf size={16} />
                {t('reports.downloadPDF')}
              </button>
             </div>
           </div>
         </div>
       </div>

             {/* Report Results */}
       <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
         {/* Table Header */}
         <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
           <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.report')}</h1>
         </div>
         
         {reportGenerated ? (
           <>
             {/* Sayfa boyutu seçici */}
             <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
               <div className="flex items-center gap-2">
                 <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
                 <select
                   value={itemsPerPage}
                   onChange={(e) => handlePageSizeChange(e.target.value)}
                   className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   {pageSizeOptions.map(size => (
                     <option key={size} value={size}>
                       {size}
                     </option>
                   ))}
                 </select>
                 <span className="text-sm text-gray-600 dark:text-gray-300">{t('table.records')}</span>
               </div>
               <div className="flex items-center gap-4">
                 <span className="text-sm text-gray-600 dark:text-gray-300">
                   {t('table.totalRecordsFound', { count: reportData.length })}
                 </span>
               </div>
             </div>

            {reportData.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.companyName')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.open')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.inProgress')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.closed')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.contractCount')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.contactPerson')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.phone')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.email')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.registrationDate')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentData.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {item.customer_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              {item.open_cases}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.in_progress_cases}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              {item.closed_cases}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="font-medium">{item.total_contracts}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.customer_contact || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.customer_contact_phone || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.customer_contact_email || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {new Date(item.created_at).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {currentData.map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-1 gap-3">
                        {/* Company Name */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.companyName')}:
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white text-right">
                            {item.customer_name}
                          </span>
                        </div>

                        {/* Cases Row */}
                        <div className="grid grid-cols-3 gap-2">
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.open')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              {item.open_cases}
                            </span>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.inProgress')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.in_progress_cases}
                            </span>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.closed')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              {item.closed_cases}
                            </span>
                          </div>
                        </div>

                        {/* Contract Count */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.contractCount')}:
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.total_contracts}
                          </span>
                        </div>

                        {/* Contact Person */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.contactPerson')}:
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                            {item.customer_contact || '-'}
                          </span>
                        </div>

                        {/* Phone */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.phone')}:
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                            {item.customer_contact_phone || '-'}
                          </span>
                        </div>

                        {/* Email */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.email')}:
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 text-right truncate">
                            {item.customer_contact_email || '-'}
                          </span>
                        </div>

                        {/* Registration Date */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.registrationDate')}:
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                            {new Date(item.created_at).toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                 {/* Sayfalama */}
                 {totalPages > 1 && (
                   <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                     <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between mb-6">
                       <div>
                         <p className="text-sm text-gray-700 dark:text-gray-300">
                           {t('table.showing')}{' '}
                           <span className="font-medium">{indexOfFirstItem + 1}</span>
                           {' '}-{' '}
                           <span className="font-medium">
                             {Math.min(indexOfLastItem, reportData.length)}
                           </span>{' '}
                           / {t('table.total')}{' '}
                           <span className="font-medium">{reportData.length}</span>{' '}
                           {t('table.records')}
                         </p>
                       </div>
                       <div>
                         <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                           <button
                             onClick={() => handlePageChange(currentPage - 1)}
                             disabled={currentPage === 1}
                             className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                               currentPage === 1
                                 ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                 : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                             }`}
                           >
                             {t('table.previous')}
                           </button>
                           {[...Array(totalPages)].map((_, index) => (
                             <button
                               key={index + 1}
                               onClick={() => handlePageChange(index + 1)}
                               className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                                 currentPage === index + 1
                                   ? 'bg-blue-600 text-white'
                                   : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                               }`}
                             >
                               {index + 1}
                             </button>
                           ))}
                           <button
                             onClick={() => handlePageChange(currentPage + 1)}
                             disabled={currentPage === totalPages}
                             className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                               currentPage === totalPages
                                 ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                 : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                             }`}
                           >
                             {t('table.next')}
                           </button>
                         </nav>
                       </div>
                     </div>
                   </div>
                 )}
               </>
             ) : (
               <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                 <p>{t('reports.noDataFound')}</p>
               </div>
             )}
           </>
         ) : (
           <div className="text-center py-10 text-gray-500 dark:text-gray-400">
             <p>{t('reports.useFiltersToGenerate')}</p>
           </div>
         )}
       </div>

      {/* Validation Popup */}
      {showValidationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="bg-red-500 text-white p-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                {t('reports.filterRequired')}
              </h3>
              <button 
                onClick={() => setShowValidationPopup(false)}
                className="text-white hover:text-red-200"
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t('reports.filterRequiredMessage')}
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowValidationPopup(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  {t('common.understood')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyReportPage; 